import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, User, Loader2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password }, { withCredentials: true });
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#0A0D14] px-4 py-3 pl-11 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-amber-400/60";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0D14] px-5">
      <div className="noise-overlay" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm"
      >
        <div className="mb-8 flex justify-center">
          <a href="/" data-testid="admin-login-logo-link" aria-label="Volver al inicio">
            <Logo />
          </a>
        </div>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="gold-trace rounded-2xl border border-white/10 bg-[#121722]/90 p-8 backdrop-blur-xl"
        >
          <h1 className="font-display text-center text-2xl font-bold text-white">Panel de control</h1>
          <p className="mt-1 text-center text-xs uppercase tracking-[0.22em] text-slate-500">Acceso restringido</p>

          <div className="relative mt-7">
            <User className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Usuario"
              autoComplete="username"
              required
              className={inputCls}
            />
          </div>
          <div className="relative mt-4">
            <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              autoComplete="current-password"
              required
              className={inputCls}
            />
          </div>

          {error && (
            <div data-testid="admin-login-error" className="mt-4 rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-2.5 text-center text-xs text-red-300">
              {error}
            </div>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-7 py-3 text-sm font-bold text-slate-950 transition-colors duration-300 hover:bg-amber-300 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Entrar
          </button>
        </form>
      </motion.div>
    </div>
  );
}
